"use client"

import axios from "axios"
import { usePathname, useSearchParams } from "next/navigation"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { Culture, getNonUmbracoUrl } from "utils"
import { transformProperties } from "utils/simplifyData"

interface PageAnchor {
  id: string
  title?: string
}

interface JobContextItem {
  adamTotalID?: string
  text?: string
}

interface Job {
  id: string
  title: string
  description: string
  areaId?: number
  areaName?: string
  companyId?: number
  companyName?: string | null
  jobName?: string
  orderId?: number
  professionId?: number
  professionName?: string
  scopeJobId?: number
  scopeJobName?: string | null
  startAdvertisingDate?: string
  requirements?: string
}

interface JobsContextType {
  currentJob: string | null
  setCurrentJob: (job: string | null) => void
  jobsFilter: string
  setJobsFilter: (filter: string) => void
  culture: Culture
  allJobs: Job[]
  filteredJobs: Job[]
  isLoading: boolean
  error: string | null
  searchQuery: string
  setSearchQuery: (query: string) => void
  searchJobs: (query: string) => void
  refetchJobs: () => Promise<void>
  pageAnchors?: PageAnchor[]
  categoriesItems: any[]
  setCategoriesItems: (items: any[]) => void
  showSuccessDialog: boolean
  setShowSuccessDialog: (show: boolean) => void
  showFailedDialog: boolean
  setShowFailedDialog: (show: boolean) => void
}

const JobsContext = createContext<JobsContextType | undefined>(undefined)

interface JobsProviderProps {
  children: ReactNode
  culture: Culture
  pageAnchors?: PageAnchor[]
}

export function JobsProvider({
  children,
  culture,
  pageAnchors = [],
}: JobsProviderProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [currentJob, setCurrentJob] = useState<string | null>(null)
  const [jobsFilter, setJobsFilter] = useState<string>("all")
  const [allJobs, setAllJobs] = useState<Job[]>([])
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>("")
  const [categoriesItems, setCategoriesItems] = useState<any[]>([])
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false)
  const [showFailedDialog, setShowFailedDialog] = useState<boolean>(false)
  const [isPageReady, setIsPageReady] = useState<boolean>(false)

  // Fetch jobs from our jobs-api endpoint
  const fetchJobs = useCallback(async (params: any = {}) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await axios.post("/api/jobs/jobs-api", {
        ...params,
        requestPage: 0,
        pageSize: 100,
      })
      // Check for jobs array in the response (new format)
      if (response.data && Array.isArray(response.data.jobs)) {
        const processedJobs: Job[] = response.data.jobs.map((item: any) => {
          return {
            id:
              item.orderId?.toString() ||
              Math.random().toString(36).substr(2, 9),
            title: item.jobName,
            description: item.description || "",
            requirements: item.requirements || "",
            areaId: item.areaId,
            areaName: item.areaName,
            companyId: item.companyId,
            companyName: item.companyName,
            jobName: item.jobName,
            orderId: item.orderId,
            professionId: item.professionId,
            professionName: item.professionName,
            scopeJobId: item.scopeJobId,
            scopeJobName: item.scopeJobName,
            startAdvertisingDate: item.startAdvertisingDate,
          }
        })
        setAllJobs(processedJobs)
        setFilteredJobs(processedJobs)
      }
      // Fallback for legacy format with items array
      else if (response.data && Array.isArray(response.data.items)) {
        const processedJobs: Job[] = response.data.items.map((item: any) => {
          return {
            id: item.id || Math.random().toString(36).substr(2, 9),
            title: item.title || item.name || item.jobName || "",
            description: item.description || "",
            requirements: item.requirements || "",
            professionName: item.professionName || "",
            areaName: item.areaName || "",
            scopeJobName: item.scopeJobName || "",
          }
        })

        setAllJobs(processedJobs)
        setFilteredJobs(processedJobs)
      } else {
        console.error("Unexpected response format:", response.data)
        throw new Error("Invalid response format")
      }
    } catch (err) {
      console.error("Error fetching jobs:", err)
      setError("Failed to load job listings")
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Search jobs function
  const searchJobs = useCallback(
    (query: string) => {
      setSearchQuery(query)

      if (!query.trim()) {
        setFilteredJobs(allJobs)
        return
      }

      const normalizedQuery = query.toLowerCase().trim()
      const results = allJobs.filter(
        (job) =>
          (job.title && job.title.toLowerCase().includes(normalizedQuery)) ||
          (job.jobName &&
            job.jobName.toLowerCase().includes(normalizedQuery)) ||
          (job.description &&
            job.description.toLowerCase().includes(normalizedQuery)) ||
          (job.professionName &&
            job.professionName.toLowerCase().includes(normalizedQuery)) ||
          (job.areaName &&
            job.areaName.toLowerCase().includes(normalizedQuery)),
      )
      setFilteredJobs(results)
    },
    [allJobs],
  )

  // Function to refetch jobs with current filter
  const refetchJobs = useCallback(async () => {
    if (!isPageReady) return

    const professionIdNumber = parseInt(jobsFilter, 10)
    await fetchJobs(
      professionIdNumber ? { professionIds: [professionIdNumber] } : {},
    )
  }, [fetchJobs, jobsFilter, isPageReady])

  // Mark page as ready after initial render
  useEffect(() => {
    setIsPageReady(true)
  }, [])

  // Main effect to handle job fetching based on URL and categories
  useEffect(() => {
    if (!isPageReady) return

    let shouldFetch = false
    let fetchParams: any = {}

    // Check if current URL matches any category item's link and filter by adamTotalID if it does
    if (pathname && categoriesItems && categoriesItems.length > 0) {
      const matchingItem = categoriesItems.find((item) => {
        const properties = transformProperties(item?.properties)
        if (properties?.link?.links && properties.link.links.length > 0) {
          const itemLink = getNonUmbracoUrl(properties.link.links[0]?.url)
          return (
            itemLink &&
            pathname.replace(/\/$/, "") === itemLink.replace(/\/$/, "")
          )
        }
        return false
      })

      if (matchingItem) {
        const { adamTotalID } = transformProperties(matchingItem?.properties)
        if (adamTotalID?.value || adamTotalID?.value == 0) {
          const professionId = parseInt(adamTotalID.value, 10)
          if (!isNaN(professionId)) {
            setJobsFilter(adamTotalID.value)
            fetchParams = adamTotalID?.value !== 0 && {
              professionIds: [professionId],
            }
            shouldFetch = true
          } else {
            setJobsFilter("all")
            shouldFetch = true
          }
        }
      } else {
        // No matching category found, fetch all jobs
        shouldFetch = true
      }
    } else {
      // No categories or pathname, fetch all jobs
      shouldFetch = true
    }

    if (shouldFetch) {
      fetchJobs(fetchParams)
    }
  }, [isPageReady, pathname, categoriesItems, fetchJobs])

  // Check for title in URL parameters and use it as search query
  useEffect(() => {
    if (!isPageReady) return

    const titleParam = searchParams.get("title")
    if (titleParam) {
      searchJobs(decodeURIComponent(titleParam))
    }
  }, [isPageReady, searchParams, searchJobs])

  const value = {
    currentJob,
    setCurrentJob,
    jobsFilter,
    setJobsFilter,
    culture,
    allJobs,
    filteredJobs,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    searchJobs,
    refetchJobs,
    pageAnchors,
    categoriesItems,
    setCategoriesItems,
    showSuccessDialog,
    setShowSuccessDialog,
    showFailedDialog,
    setShowFailedDialog,
  }

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>
}

export function useJobsContext() {
  const context = useContext(JobsContext)
  if (context === undefined) {
    throw new Error("useJobsContext must be used within a JobsProvider")
  }
  return context
}
